import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import {
  getClientIdentityApi,
  getClientsDeliveryAddressLogsApi,
  updateClientIdentityStatusApi,
} from "app/services/clients";
import {
  ClientAction,
  ClientDeliveryAddressLog,
  ClientDeliveryAddressLogsApiResponse,
  ClientIdentity,
} from "app/types/clients";
import { alertMessage } from "../actions/common";
import { User } from "app/types/subscriptionsManager";
import { PaginationInterfaceLF } from "app/types";
import { RootState } from "../reducers/hooks";

interface InitialState {
  identity: ClientIdentity;
  clientDeliveryAddressLogs: ClientDeliveryAddressLog[];
  clientDeliveryAddressLogsLoading: boolean;
  clientDeliveryAddressLogsPagination?: PaginationInterfaceLF;
  clientDeliveryAddressLogsRowPerPage?: number;
}

const initialState: InitialState = {
  identity: null,
  clientDeliveryAddressLogs: [],
  clientDeliveryAddressLogsLoading: false,
  clientDeliveryAddressLogsPagination: null,
  clientDeliveryAddressLogsRowPerPage: 20,
};

export const clientsSlice = createSlice({
  name: "clientSlice",
  initialState,
  reducers: {
    setClientIdentity: (
      state,
      action: PayloadAction<ClientIdentity | null>,
    ) => {
      state.identity = action.payload;
    },
    setClientDeliveryAddressLogsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.clientDeliveryAddressLogsLoading = action.payload;
    },
    setClientDeliveryAddressLogs: (
      state,
      action: PayloadAction<{
        logs: ClientDeliveryAddressLog[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.clientDeliveryAddressLogs = action.payload.logs;
      state.clientDeliveryAddressLogsPagination = action.payload.pagination;
    },
    setClientDeliveryAddressLogsRowPerPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.clientDeliveryAddressLogsRowPerPage = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setClientIdentity,
  setClientDeliveryAddressLogs,
  setClientDeliveryAddressLogsLoading,
  setClientDeliveryAddressLogsRowPerPage,
} = clientsSlice.actions;

export default clientsSlice.reducer;

export const getClientIdentityAction =
  (clientId: number) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await getClientIdentityApi(clientId);
      const identity = data.data;
      const users = data.included.users;

      // find user by id in logs from included users
      const logs = identity.logs.map((log) => {
        const user = users.find((user) => user.userId === log.userId);
        return { ...log, user };
      });

      const clientIdentity = { ...identity, logs };
      dispatch(setClientIdentity(clientIdentity));
    } catch (error) {
      // Client has no identity
      dispatch(setClientIdentity(null));
    }
  };

type UpdateClientIdentityStatusRequestPayload = {
  identityId: number;
  type: string;
  enable: boolean;
  reason: string;
  clientId: number;
  successCallback: () => void;
};
export const updateClientIdentityStatusAction =
  ({
    identityId,
    type,
    enable,
    reason,
    clientId,
    successCallback,
  }: UpdateClientIdentityStatusRequestPayload) =>
  async (dispatch: AppDispatch) => {
    // Update client identity status
    try {
      const { data } = await updateClientIdentityStatusApi({
        identityId,
        type,
        enable,
        reason,
      });

      if (data.data.message) {
        // We refresh the client identity data
        dispatch(getClientIdentityAction(clientId));
        successCallback();
      }
    } catch (error) {
      dispatch(alertMessage("error", "Error updating client identity status"));
    }
  };

export const getClientDeliveryAddressLogsAction =
  (clientId: number, continuationToken: string | null = null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { clientDeliveryAddressLogsRowPerPage } = getState().clientsSlice;
      let query = `?action=${ClientAction.AddAddress}&action=${ClientAction.UpdateAddress}&limit=${clientDeliveryAddressLogsRowPerPage}`;
      if (continuationToken)
        query += `&continuationToken=${encodeURIComponent(continuationToken)}`;

      dispatch(setClientDeliveryAddressLogsLoading(true));
      const { data }: { data: ClientDeliveryAddressLogsApiResponse } =
        await getClientsDeliveryAddressLogsApi(clientId, query);

      dispatch(setClientDeliveryAddressLogsLoading(false));

      const includedUsers: User[] = data.included?.users ?? [];

      const mappedLogs = data.data.map((log) => {
        const user = includedUsers.find((u) => u.id === log.userId);
        return {
          ...log,
          actionBy: user?.name ?? null,
        };
      });

      dispatch(
        setClientDeliveryAddressLogs({
          logs: mappedLogs,
          pagination: data.pagination,
        }),
      );
    } catch (error) {
      dispatch(setClientDeliveryAddressLogsLoading(false));
      dispatch(
        setClientDeliveryAddressLogs({
          logs: [],
          pagination: null,
        }),
      );
    }
  };
